import { create } from 'zustand';
import { api, AdminProduct, ProductUpdatePayload } from '../lib/api';

interface ProductState {
  products: AdminProduct[];
  total: number;
  isLoading: boolean;
  error: string | null;
  statusFilter: string | null;
  searchQuery: string;

  setStatusFilter: (status: string | null) => void;
  setSearchQuery: (query: string) => void;
  fetchProducts: () => Promise<void>;
  approveProduct: (id: number) => Promise<void>;
  rejectProduct: (id: number, reason?: string) => Promise<void>;
  updateProduct: (id: number, payload: ProductUpdatePayload) => Promise<void>;
  deleteProduct: (id: number) => Promise<void>;
}

export const useProductStore = create<ProductState>((set, get) => ({
  products: [],
  total: 0,
  isLoading: false,
  error: null,
  statusFilter: null,
  searchQuery: '',

  setStatusFilter: (status) => set({ statusFilter: status }),
  setSearchQuery: (query) => set({ searchQuery: query }),

  fetchProducts: async () => {
    const { statusFilter, searchQuery } = get();
    set({ isLoading: true, error: null });
    try {
      const result = await api.products.list({
        status_filter: statusFilter || undefined,
        search: searchQuery || undefined,
        limit: 200,
      });
      set({
        products: result?.products || [],
        total: result?.total || 0,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch products';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  approveProduct: async (id: number) => {
    const updated = await api.products.approve(id);
    set((state) => ({
      products: state.products.map((p) => (p.id === id ? updated : p)),
    }));
  },

  rejectProduct: async (id: number, reason?: string) => {
    const updated = await api.products.reject(id, reason);
    set((state) => ({
      products: state.products.map((p) => (p.id === id ? updated : p)),
    }));
  },

  updateProduct: async (id: number, payload: ProductUpdatePayload) => {
    const updated = await api.products.update(id, payload);
    set((state) => ({
      products: state.products.map((p) => (p.id === id ? updated : p)),
    }));
  },

  deleteProduct: async (id: number) => {
    await api.products.delete(id);
    set((state) => ({
      products: state.products.filter((p) => p.id !== id),
      total: Math.max(0, state.total - 1),
    }));
  },
}));

export function useProducts() {
  const store = useProductStore();
  return {
    products: store.products,
    total: store.total,
    isLoading: store.isLoading,
    error: store.error,
    statusFilter: store.statusFilter,
    searchQuery: store.searchQuery,
    setStatusFilter: store.setStatusFilter,
    setSearchQuery: store.setSearchQuery,
    fetchProducts: store.fetchProducts,
    approveProduct: store.approveProduct,
    rejectProduct: store.rejectProduct,
    updateProduct: store.updateProduct,
    deleteProduct: store.deleteProduct,
  };
}
